import { useState } from "react";
import type { ReactNode } from "react";
import { useAuth } from "../hooks/useAuth";
import { useI18n } from "../i18n";
import LanguageToggle from "./LanguageToggle";

// Wraps admin-only pages. Shows a password prompt until the visitor is logged
// in as admin; the session token persists in localStorage across refreshes.
export default function AdminGate({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const { isAdmin, isLoading, login } = useAuth();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (isAdmin) return <>{children}</>;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-linen-50 text-charcoal-muted">
        {t.common.loading}
      </div>
    );
  }

  const handleSubmit = async () => {
    if (!password || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await login(password);
    } catch {
      setError(t.gate.wrongPassword);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-linen-50 px-4">
      <div className="w-full max-w-sm rounded-softer bg-white p-6 shadow-card">
        <div className="flex items-start justify-between">
          <h1 className="font-serif text-2xl text-charcoal">{t.gate.title}</h1>
          <LanguageToggle className="-mr-2 -mt-1" />
        </div>
        <p className="mt-1 text-sm text-charcoal-muted">
          {t.gate.subtitle}
        </p>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          placeholder={t.gate.passwordPlaceholder}
          autoFocus
          className="mt-4 w-full min-h-[44px] rounded-soft border border-linen-200 bg-linen-50 px-4 py-2.5 text-charcoal placeholder:text-linen-400 focus:outline-none focus:ring-2 focus:ring-sage-400"
        />
        <button
          onClick={handleSubmit}
          disabled={!password || isSubmitting}
          className="mt-3 w-full min-h-[48px] rounded-softer bg-sage-500 font-semibold text-white transition-colors hover:bg-sage-600 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isSubmitting ? t.gate.checking : t.gate.submit}
        </button>
        {error && <p className="mt-2 text-center text-sm text-red-600">{error}</p>}
      </div>
    </div>
  );
}
